import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import styled from 'styled-components';
import {motion} from 'framer-motion';
import Hero from '../common/Hero';
import DancingShield from '../common/DancingShield';

const HomeContainer = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem;
`;

const FeaturesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    margin: 4rem 0;
`;

const FeatureCard = styled(motion.div)`
    background: white;
    border-radius: 10px;
    padding: 2rem;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
    transition: transform 0.3s ease;

    &:hover {
        transform: translateY(-5px);
    }

    h3 {
        color: #333;
        margin-bottom: 1rem;
    }

    p {
        color: #666;
        line-height: 1.6;
    }

    a {
        display: inline-block;
        margin-top: 1rem;
        color: #6c5ce7;
        text-decoration: none;
        font-weight: 500;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const UpcomingEvents = styled.div`
    margin: 4rem 0;

    h2 {
        font-size: 2rem;
        margin-bottom: 2rem;
        color: #333;
    }
`;

const EventList = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 2rem;
`;

const EventCard = styled.div`
    background: white;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);

    .event-image {
        height: 200px;
        background-color: #f0f0f0;
        background-size: cover;
        background-position: center;
    }

    .event-content {
        padding: 1.5rem;

        h3 {
            margin-bottom: 0.5rem;
        }

        .event-date {
            color: #6c5ce7;
            font-weight: 500;
            margin-bottom: 1rem;
            display: block;
        }

        p {
            color: #666;
            margin-bottom: 1rem;
        }

        button {
            background: #6c5ce7;
            color: white;
            border: none;
            padding: 0.75rem 1.5rem;
            border-radius: 5px;
            cursor: pointer;
            font-weight: 500;
            transition: background 0.3s ease;

            &:hover {
                background: #5a4ad1;
            }
        }
    }
`;

const PaginationContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    margin: 3rem 0;
    flex-wrap: wrap;
`;

const PaginationButton = styled.button`
    background: ${props => props.active ? '#6c5ce7' : 'white'};
    color: ${props => props.active ? 'white' : '#6c5ce7'};
    border: 2px solid #6c5ce7;
    padding: 0.5rem 1rem;
    border-radius: 5px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.3s ease;
    min-width: 40px;

    &:hover:not(:disabled) {
        background: #6c5ce7;
        color: white;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const PageInfo = styled.span`
    color: #666;
    font-weight: 500;
`;

const ArticleModal = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    padding: 2rem;
`;

const ArticleModalContent = styled(motion.div)`
    background: white;
    border-radius: 10px;
    max-width: 800px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    padding: 2rem;
    position: relative;

    .close-button {
        position: absolute;
        top: 1rem;
        right: 1rem;
        background: none;
        border: none;
        font-size: 1.5rem;
        cursor: pointer;
    }

    h2 {
        margin-bottom: 1.5rem;
        color: #333;
    }

    .recipe-section {
        margin-bottom: 2rem;

        h3 {
            color: #6c5ce7;
            margin-bottom: 1rem;
            margin-top: 1rem;
        }

        p {
            margin-top: 1rem;
        }

        ul, ol {
            padding-left: 1.5rem;

            li {
                margin-bottom: 0.5rem;
            }
        }
    }
`;

const NoEvents = styled.div`
    text-align: center;
    padding: 3rem;
    color: #666;
    font-size: 1.1rem;
    grid-column: 1 / -1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
`;

const NoEventsContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3rem;
    flex-wrap: wrap;

    @media (max-width: 768px) {
        gap: 2rem;
    }
`;

const LoadingMessage = styled.div`
    text-align: center;
    padding: 3rem;
    color: #6c5ce7;
    font-size: 1.2rem;
    font-weight: 500;
    grid-column: 1 / -1;
`;

const ErrorMessage = styled.div`
    text-align: center;
    padding: 2rem;
    color: #e74c3c;
    background: #fadbd8;
    border-radius: 5px;
    margin-bottom: 2rem;
`;

const Home = () => {
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [latestNews, setLatestNews] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    const API_URL = 'https://z184yo4b1e.execute-api.eu-west-1.amazonaws.com/prod/mlalazi/events';
    const ITEMS_PER_PAGE = 6;

    // Fetch Events from API
    useEffect(() => {
        const fetchEvents = async () => {
            setLoading(true);
            setError(null);

            try {
                const params = new URLSearchParams({
                    page: currentPage,
                    limit: ITEMS_PER_PAGE,
                    type: 'home',
                    sort: 'desc',
                    search: ''
                });

                const response = await fetch(`${API_URL}?${params.toString()}`);

                if (!response.ok) {
                    throw new Error('Failed to fetch events');
                }

                const data = await response.json();
                setLatestNews(data.data || []);
                setTotalPages(data.meta?.totalPages || 1);
                setTotalItems(data.meta?.totalItems || 0);

            } catch (err) {
                console.error("Error fetching events:", err);
                setError("Sorry, we could not fetch the latest news for you. Check your network and load this page again...");
                setLatestNews([]);
            } finally {
                setLoading(false);
            }
        };

        fetchEvents();
    }, [currentPage]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const renderPaginationButtons = () => {
        const buttons = [];
        const maxVisibleButtons = 5;

        let startPage = Math.max(1, currentPage - Math.floor(maxVisibleButtons / 2));
        let endPage = Math.min(totalPages, startPage + maxVisibleButtons - 1);

        if (endPage - startPage < maxVisibleButtons - 1) {
            startPage = Math.max(1, endPage - maxVisibleButtons + 1);
        }

        // First page button
        if (startPage > 1) {
            buttons.push(
                <PaginationButton key={1} onClick={() => handlePageChange(1)}>
                    1
                </PaginationButton>
            );
            if (startPage > 2) {
                buttons.push(<span key="ellipsis-start">...</span>);
            }
        }

        // Page number buttons
        for (let i = startPage; i <= endPage; i++) {
            buttons.push(
                <PaginationButton
                    key={i}
                    active={currentPage === i}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </PaginationButton>
            );
        }

        // Last page button
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                buttons.push(<span key="ellipsis-end">...</span>);
            }
            buttons.push(
                <PaginationButton key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </PaginationButton>
            );
        }

        return buttons;
    };

    return (
        <>
            <Hero
                title="Welcome to Our Clan!"
                subtitle="Connecting generations, preserving our legacy"
                backgroundImage="/assets/images/logo.jpg"
            />

            <HomeContainer>
                <motion.div
                    initial={{opacity: 0, y: 20}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.5}}
                >
                    <FeaturesGrid>
                        <FeatureCard whileHover={{scale: 1.03}}>
                            <h3>Our History</h3>
                            <p>Explore our clan's rich history dating back generations. Discover the stories,
                                challenges, and triumphs that shaped who we are today.</p>
                            <Link to="/history">Learn about our roots →</Link>
                        </FeatureCard>

                        <FeatureCard whileHover={{scale: 1.03}}>
                            <h3>Cultural Heritage</h3>
                            <p>Immerse yourself in our cultural traditions, customs, and values that have been passed
                                down through generations.</p>
                            <Link to="/heritage">Explore our heritage →</Link>
                        </FeatureCard>

                        <FeatureCard whileHover={{scale: 1.03}}>
                            <h3>Clan Businesses</h3>
                            <p>Discover the entrepreneurial spirit of our clan through the various businesses
                                established and run by clan members.</p>
                            <Link to="/businesses">View our enterprises →</Link>
                        </FeatureCard>
                    </FeaturesGrid>

                    {error && <ErrorMessage>{error}</ErrorMessage>}

                    <UpcomingEvents>
                        <h2>Latest News</h2>
                        <EventList>
                            {loading ? (
                                <LoadingMessage>Loading latest news...</LoadingMessage>
                            ) : latestNews.length > 0 ? (
                                latestNews.map(event => (
                                    <EventCard key={event.id}>
                                        <div className="event-image" style={{backgroundImage: `url(${event.image})`}}></div>
                                        <div className="event-content">
                                            <h3>{event.title}</h3>
                                            <span className="event-date">{event.date}</span>
                                            <p>{event.description}</p>
                                            <button onClick={() => setSelectedArticle(event)}>View Article</button>
                                        </div>
                                    </EventCard>
                                ))
                            ) : (
                                <NoEvents>
                                    <NoEventsContent>
                                        <DancingShield />
                                        <div>
                                            <p>Sorry, there is no latest news at this time.To view our past news articles or upcoming events, click{' '}
                                                <Link to="/events">here..</Link>
                                            </p>
                                        </div>
                                    </NoEventsContent>
                                </NoEvents>
                            )}
                        </EventList>

                        {!loading && latestNews.length > 0 && totalPages > 1 && (
                            <PaginationContainer>
                                <PaginationButton
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    ← Previous
                                </PaginationButton>

                                {renderPaginationButtons()}

                                <PaginationButton
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next →
                                </PaginationButton>

                                <PageInfo>
                                    Page {currentPage} of {totalPages} ({totalItems} items)
                                </PageInfo>
                            </PaginationContainer>
                        )}
                    </UpcomingEvents>
                </motion.div>
            </HomeContainer>

            {selectedArticle && (
                <ArticleModal onClick={() => setSelectedArticle(null)}>
                    <ArticleModalContent
                        onClick={e => e.stopPropagation()}
                        initial={{opacity: 0, y: 50}}
                        animate={{opacity: 1, y: 0}}
                    >
                        <button className="close-button" onClick={() => setSelectedArticle(null)}>✕</button>
                        <h2>{selectedArticle.title}</h2>
                        <p>Reported By: <i>{selectedArticle.author}</i></p>
                        {selectedArticle.sections?.map((section, index) => (
                            <div className="recipe-section" key={index}>
                                <p>{section.content}</p>
                                <h3>{section.title}</h3>
                                <ul>
                                    {section.sentences?.map((sentence, idx) => (
                                        <li key={sentence.id || idx}>{sentence.text}</li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </ArticleModalContent>
                </ArticleModal>
            )}
        </>
    );
};

export default Home;